/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Directive, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { takeUntil, tap } from 'rxjs/operators';

import { MENU_SERVICE_TOKEN } from '@ups/common';
import { AuthenticationService } from '@ups/security';
import { BaseComponent, environment } from '@ups/xplat/core';
import { HRRMService } from '@ups/xplat/api/services';
import { registerLicense } from '@syncfusion/ej2-base';
import {
  AppVersionValue,
  NotificationsViewService,
  WebVersionEnforcerService,
} from '../services';

@Directive()
export abstract class AppBaseComponent extends BaseComponent {
  /**
   * Define common root app component behavior for all web apps here
   */
  employeeVpService: HRRMService;
  injector = inject(Injector);
  menuService = inject(MENU_SERVICE_TOKEN, { optional: true });
  auth = inject(AuthenticationService);
  router = inject(Router);
  // NOTE: just to force DI to initialize instance
  notificationsService = inject(NotificationsViewService);
  versionEnforcerService = inject(WebVersionEnforcerService);

  constructor() {
    super();
    registerLicense(environment.syncfusionLicenseKey);
    this.employeeVpService = this.injector.get(HRRMService);

    // this.router.events.subscribe((_) => console.log('router event', _));

    // Only get Auth0 token if NOT on a public route
    // Use window.location instead of router.url because router may not be initialized yet
    const actualUrl =
      typeof window !== 'undefined'
        ? window.location.pathname
        : this.router.url;
    // /login counts as public here: firing a silent Auth0 token fetch on the
    // login page throws login_required for exactly the visitor the page
    // exists for, and that error is what the global handler used to answer
    // by redirecting to Auth0 interactive login, yanking users off the
    // dual-issuer (Entra + Auth0) login screen mid-typing. The login page
    // forwards already-authenticated sessions itself (forwardAuthenticatedUser).
    const isPublicRoute =
      actualUrl.startsWith('/public/') || actualUrl.startsWith('/login');

    // An Entra (MSAL) session has no Auth0 token to fetch: this call can
    // only ever throw login_required there, which is the error that used to
    // feed the global handler's Auth0 redirect and hijack the session right
    // after an Entra sign-in landed on the home page. Same key as the
    // login-issuer store (web/core cannot depend on web/features).
    const isEntraSession =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('ups.login.issuer') === 'entra';

    if (!isPublicRoute && !isEntraSession) {
      console.log(
        '[AppBaseComponent] Getting Auth0 token for protected route:',
        actualUrl
      );
      this.auth
        .getAccessTokenSilently()
        .pipe(
          takeUntil(this.destroy$),
          tap((x) => localStorage.setItem('id_token', x))
        )
        .subscribe({
          error: (err) => {
            console.log(
              '[AppBaseComponent] Failed to get Auth0 token (expected for public routes):',
              err
            );
          },
        });
    } else {
      console.log(
        '[AppBaseComponent] Skipping Auth0 token for public route:',
        actualUrl
      );
    }
  }

  /**
   * Override this method to extend behavior.
   */

  // eslint-disable-next-line no-unused-vars
  onEmployeeLoaded(profile: any) {}

  protected enforceLatestWebVersion(
    versionFile: { version?: AppVersionValue } | null | undefined,
    appKey: string
  ) {
    return this.versionEnforcerService.enforceLatestVersionFromFile(
      versionFile,
      appKey
    );
  }
}
